/**
 * Centralised TanStack Query key factory.
 *
 * Replaces ~70 inline string-array literals across /admin and the candidate
 * flow. Each factory returns a tuple shaped EXACTLY like the original inline
 * key — refactor is mechanical, no cache identity drift.
 *
 * Conventions:
 *   - `*All()` returns the prefix-only key for invalidation. TanStack Query
 *     matches invalidations by prefix, so `queryKeys.libraryQuestions.all()`
 *     still invalidates every parameterised variant below it.
 *   - Parameterised entries (`list`, `byToken`, `byId`, ...) return the
 *     fully-filled key including the prefix. Pass the exact value the call
 *     site previously inlined — undefined-or-empty-string normalisation
 *     lives at the call site (preserves cache identity verbatim).
 *
 * Why: prevents the typo class of cache-miss bug where one site spells the
 * key differently from another. Lives next to api.ts.
 */
export const queryKeys = {
  // ─── Workspace-global (no args) ──────────────────────────────────────
  tags: () => ["tags"] as const,
  jobs: () => ["jobs"] as const,
  adminOverview: () => ["admin-overview"] as const,
  workspaceTests: () => ["workspace-tests"] as const,
  pendingFollowUps: () => ["pending-follow-ups"] as const,
  // CP-23 cross-test candidate directory. `params` (q / limit / offset) is
  // part of the key so every search + page is its own cache entry;
  // `candidatesAll()` is the prefix to invalidate them all at once.
  candidatesAll: () => ["candidates"] as const,
  candidates: (params: object = {}) => ["candidates", params] as const,
  billingMe: () => ["billing", "me"] as const,
  billingConfig: () => ["billing", "config"] as const,
  // FEAT-2 (MVP 5.5): the live Paddle subscription + a change's price quote.
  billingSubscription: () => ["billing", "subscription"] as const,
  planChangePreview: (plan: string) => ["billing", "change-plan-preview", plan] as const,
  // FEAT-9: the AI usage meter — refreshed after every AI call (api.ts).
  aiUsage: () => ["billing", "ai-usage"] as const,
  team: () => ["team"] as const,
  // FEAT-39: the owner's role × permission matrix (Settings → Roles & permissions).
  rolePermissions: () => ["team", "role-permissions"] as const,
  companySettings: () => ["company", "settings"] as const,
  branding: () => ["company", "branding"] as const,
  // EF-1: the candidate welcome (video + message) — its own resource beside
  // the email branding, so an upload never refetches the whole branding card.
  welcome: () => ["company", "welcome"] as const,
  // Live email preview — keyed by email type + a hash of the draft so unrelated
  // re-renders don't refetch and each draft variant is its own cache entry.
  brandingPreview: (emailType: string, draftHash: string) =>
    ["company", "branding-preview", emailType, draftHash] as const,
  // Prefix-only key for invalidating EVERY branding-preview variant at once
  // (TanStack matches invalidations by prefix). Sourced here so the Settings
  // invalidations never hand-type the literal that `brandingPreview` produces.
  brandingPreviewAll: () => ["company", "branding-preview"] as const,
  // Per-company export field selection (owner-only Settings → Export tab).
  exportSettings: () => ["company", "export-settings"] as const,
  // The recruiter's own support requests (MVP 4.0 two-way loop).
  mySupportRequests: () => ["support-requests"] as const,
  // EP-9 + EP-10. Two elements so a future ["me", ...] family invalidates by
  // prefix; kept OUT of SESSION_QUERY_KEY because a save would otherwise have
  // to invalidate the whole (large, widely-read) session payload.
  myNotificationSettings: () => ["me", "notifications"] as const,

  // ─── Back office (creator-only) ───────────────────────────────────────
  // The list keys carry a `params` object (search/filter/page) so each filtered
  // view is its own cache entry. INVALIDATE with the 2-element prefix
  // (e.g. ["backoffice", "companies"]) — TanStack matches by prefix, so that
  // clears every param variant at once.
  backofficeOverview: () => ["backoffice", "overview"] as const,
  // OPS-32: the Overview's intelligence cards, keyed by window like the
  // ledgers below. Its own key, so the header (overview) and the cards
  // refetch independently.
  backofficeIntelligence: (window: string) =>
    ["backoffice", "intelligence", window] as const,
  backofficeCompanies: (params: object = {}) =>
    ["backoffice", "companies", params] as const,
  // Shares the ["backoffice","companies"] prefix on purpose, like the
  // recruiter detail below: an approve/suspend/plan action must clear the list
  // AND the open detail page, and one prefix invalidation does both.
  backofficeCompany: (companyId: string) =>
    ["backoffice", "companies", "detail", companyId] as const,
  // FEAT-51: the detail page's Statistics tab, under the same prefix so a
  // company action (or its delete) clears it with the rest.
  backofficeCompanyStatistics: (
    companyId: string,
    window: string,
    testId?: string | null,
  ) =>
    ["backoffice", "companies", "detail", companyId, "statistics", window, testId ?? null] as const,
  // FEAT-50a: the trial-protection share shown on the Companies tab. Its own
  // key — a company action need not refetch it.
  backofficeSignupRefusals: (days: number) =>
    ["backoffice", "signup-refusals", days] as const,
  backofficeRecruiters: (params: object = {}) =>
    ["backoffice", "recruiters", params] as const,
  // Deliberately shares the ["backoffice","recruiters"] prefix: a plan change
  // must clear the list AND the open detail panel, and one prefix invalidation
  // does both.
  backofficeRecruiter: (recruiterId: string) =>
    ["backoffice", "recruiters", "detail", recruiterId] as const,
  backofficeAntiCheat: (params: object = {}) =>
    ["backoffice", "anticheat", params] as const,
  backofficeBilling: (params: object = {}) =>
    ["backoffice", "billing", params] as const,
  backofficeBillingStats: () => ["backoffice", "billing", "stats"] as const,
  backofficeLlmUsage: (window: string, params: object = {}) =>
    ["backoffice", "llm-usage", window, params] as const,
  // FEAT-49: candidcheck.com's sign-up funnel (builds, visitors, re-picks,
  // landing sign-ups), per day — read beside the AI-usage ledger.
  backofficeLandingFunnel: (days: number) =>
    ["backoffice", "landing-funnel", days] as const,
  // FEAT-12 Build 6: the code-runner meter. The runner status is its OWN key
  // (and its own route) so a window or page change never re-probes — and
  // never wakes — the sleeping heavy pool.
  backofficeCodeRunsUsage: (window: string, params: object = {}) =>
    ["backoffice", "code-runs", window, params] as const,
  backofficeRunnerStatus: () => ["backoffice", "code-runs", "runner-status"] as const,
  backofficeAtsUsage: (window: string, params: object = {}) =>
    ["backoffice", "ats-usage", window, params] as const,
  backofficeIntegrations: (window: string, params: object = {}) =>
    ["backoffice", "integrations", window, params] as const,
  // FEAT-47e: the Integrations tab's MCP-connector view.
  backofficeMcpConnector: (window: string, params: object = {}) =>
    ["backoffice", "mcp-connector", window, params] as const,
  backofficeReports: (status: string, params: Record<string, unknown> = {}) =>
    ["backoffice", "problem-reports", status, params] as const,
  // Recovery + operators (MVP 4.0).
  backofficeCompanyDirectory: (q: string) =>
    ["backoffice", "company-directory", q] as const,
  // `params` is the 4th element so the 3-element form stays a valid PREFIX —
  // the restore mutations invalidate with ["backoffice","<list>",companyId] and
  // TanStack's prefix match still clears every filter/page variant.
  backofficeDeletedItems: (companyId: string, params: object = {}) =>
    ["backoffice", "deleted-items", companyId, params] as const,
  backofficeRecoveryAudit: (companyId: string, params: object = {}) =>
    ["backoffice", "recovery-audit", companyId, params] as const,
  backofficeOperators: (params: object = {}) =>
    ["backoffice", "operators", params] as const,

  // ─── Single-arg keys ──────────────────────────────────────────────────
  // EP-5 appended `testId` AFTER `window`, deliberately: seven call sites
  // invalidate by the bare prefix (`statisticsAll()`), and a prefix stays a
  // prefix only if new args go on the end. The arg is optional and normalises
  // to null, so Home.tsx's `statistics("all")` and the unfiltered Stats page
  // still resolve to ONE cache entry — the sharing UPDATES.md records as
  // deliberate.
  /** Every statistics window at once — what a mutation invalidates (SIMP-27). */
  statisticsAll: () => ["statistics"] as const,
  statistics: (window: string, testId?: string | null) =>
    ["statistics", window, testId ?? null] as const,
  job: (jobId: string) => ["job", jobId] as const,
  jobStats: (jobId: string) => ["job-stats", jobId] as const,
  testsInJob: (jobId: string) => ["tests-in-job", jobId] as const,
  testSettings: (adminToken: string) => ["test-settings", adminToken] as const,
  attempts: (token: string) => ["admin", token, "attempts"] as const,
  // UX-7 per-test summary strip. DELIBERATELY nested UNDER attempts(): react-
  // query invalidates by key prefix, so all eight existing
  // `invalidateQueries({queryKey: attempts(token)})` calls — background
  // grading, invite, bulk invite, allow-retake, revoke, candidate delete/edit,
  // pass-mark change — refresh the strip too, with no call-site changes and no
  // way to forget one. Flattening this to a sibling key silently breaks every
  // one of them.
  testSummary: (token: string) =>
    ["admin", token, "attempts", "summary"] as const,
  // FEAT-24 named shared links. Nested UNDER attempts() for testSummary()'s
  // reason: each link carries started / completed counts, so everything that
  // already invalidates the attempts list keeps the counts honest too.
  shareLinks: (token: string) =>
    ["admin", token, "attempts", "share-links"] as const,
  preview: (token: string) => ["preview", token] as const,
  // Recruiter "preview as candidate" (MVP 4.0 item 6) — keyed by the test
  // UUID, distinct from the candidate-token `preview` above (different shape).
  candidatePreview: (token: string) => ["candidate-preview", token] as const,
  // EF-9: the platform IPIP catalogue (static per deploy).
  softSkillInstruments: () => ["soft-skill-instruments"] as const,
  // FEAT-11: one questionnaire's statements, same static lifetime. Nested
  // UNDER the catalogue key so invalidating the catalogue drops these too.
  softSkillStatements: (key: string) =>
    ["soft-skill-instruments", key, "statements"] as const,

  // ─── Multi-arg keys ──────────────────────────────────────────────────
  attempt: (token: string, id: string) =>
    ["admin", token, "attempt", id] as const,
  // FEAT-3 AI candidate summary. DELIBERATELY nested UNDER attempt(): the
  // detail page invalidates by that exact prefix after a browser-fallback
  // hidden-test grade (Detail.tsx), so the skills rollup refreshes with the
  // pills it must agree with — no call-site change, no way to forget it. Same
  // invariant as testSummary() above, and the same silent breakage if this is
  // ever flattened to a sibling key.
  attemptAiSummary: (token: string, id: string) =>
    ["admin", token, "attempt", id, "ai-summary"] as const,
  // FEAT-38: the AI reading of the attempt's interaction counts. Nested under
  // attempt() like attemptAiSummary, for the same invalidation invariant.
  interactionReading: (token: string, id: string) =>
    ["admin", token, "attempt", id, "interaction-reading"] as const,
  // FEAT-5 result email. Both nested UNDER attempt(), same invariant as
  // attemptAiSummary above: after a send, the dialog invalidates
  // attempt(token, id) and this context — the prior-send banner, the
  // "Send again" label — refreshes with the detail card's own stamp, so the
  // page can never show "not sent" beside a banner that says it was.
  attemptResultEmail: (token: string, id: string) =>
    ["admin", token, "attempt", id, "result-email"] as const,
  // Keyed by a hash of the draft so each edit is its own cache entry (the
  // Settings live preview does the same). Nested under the context key, so
  // invalidating the context also drops every stale rendering of it.
  attemptResultEmailPreview: (token: string, id: string, draftHash: string) =>
    ["admin", token, "attempt", id, "result-email", "preview", draftHash] as const,
  // EF-8: the snapshot gallery, nested UNDER attempt() like the two above so
  // an attempt-level invalidation (retake, erase) drops it too.
  attemptSnapshots: (token: string, id: string) =>
    ["admin", token, "attempt", id, "snapshots"] as const,
  // FEAT-31: one question's code replay — nested under attempt() for the same
  // reason (a retake or an erase must drop it).
  attemptReplay: (token: string, id: string, position: number) =>
    ["admin", token, "attempt", id, "replay", position] as const,
  question: (token: string, attemptId: string, position: number) =>
    ["question", token, attemptId, position] as const,
  candidateResult: (token: string, submittedAttemptId: string | null) =>
    ["candidate-result", token, submittedAttemptId] as const,
  // BUG-5: SECTION-SCOPED for the per-section review. GET /review returns the
  // WHOLE attempt, but the SCREEN that renders it is per-section — SectionReview
  // filters `answers` to [sectionStart, sectionEnd] and reads `expires_at` +
  // `next_section` as "this section's". Keying only by attempt made section
  // N-1's cached payload a legal synchronous answer for section N's screen
  // (react-query serves cached data even while `enabled` is false), so the
  // review flashed "0 of 7 submitted — 7 pending", the previous section's
  // countdown, and the wrong next-section briefing for one round-trip.
  //
  // `sectionIndex` is a CLIENT-side cache axis: the HTTP request is identical
  // with or without it. That is deliberate — this key identifies the rendered
  // VIEW, not the request.
  //
  // APPENDED, never restructured into a {all, section} family: ["review",
  // token, attemptId] stays a strict PREFIX of every section variant, so
  // Question.tsx's existing 2-arg invalidateQueries() still reaches all of them
  // by prefix match with no call-site change, and Review.tsx's global (non-
  // sectioned) key is byte-identical to what it was. Same class of invariant as
  // testSummary() above, same failure mode if someone "simplifies" it.
  review: (token: string, attemptId: string | null, sectionIndex?: number) =>
    sectionIndex === undefined
      ? (["review", token, attemptId] as const)
      : (["review", token, attemptId, sectionIndex] as const),
  // FEAT-15: GET /review again, but read by Question.tsx for the confirm on
  // the last per_question unit. Deliberately OUTSIDE the "review" prefix: every
  // save invalidates that family, and this one is fetched fresh per opening.
  submitSummary: (token: string, attemptId: string | null) =>
    ["submit-summary", token, attemptId] as const,

  // ─── Prefix families ──────────────────────────────────────────────────
  // .all() for invalidation; specific variants for fetching.
  libraryQuestions: {
    all: () => ["library-questions"] as const,
    // Filter key components ordered most-stable-first so the cache key reads
    // as a tuple of axes rather than a free-form string. `difficultyKey` /
    // `testKey` are pre-sorted-joined strings supplied by the caller (same
    // pattern as `tagKey`) — keeps cache identity invariant under filter
    // re-orderings, mirroring the AND/OR semantics the backend enforces.
    list: (
      type: string | undefined,
      search: string,
      tagKey: string,
      difficultyKey: string,
      testKey: string,
      noTest: boolean,
      archived: boolean,
      // "own" (default tenant view) | "seeded" (Library tab read-union).
      source: string = "own",
      technology: string = "",
      // CP-6: the plan filter is part of the cache identity — the same axes
      // with it on return a strictly smaller set.
      availableOnly: boolean = false,
      // FEAT-6: the coding-language facet (sorted, comma-joined), same
      // contract as `difficultyKey`.
      languages: string = "",
      // Sheet #188: the Active tab also lists the seeds the company's tests
      // use by reference — a strictly larger set under the same other axes.
      includeReferenced: boolean = false,
    ) =>
      [
        "library-questions",
        type,
        search,
        tagKey,
        difficultyKey,
        testKey,
        noTest,
        archived,
        source,
        technology,
        availableOnly,
        languages,
        includeReferenced,
      ] as const,
    picker: (
      search: string,
      typeFilter: string,
      tagKey: string,
      difficultyKey: string,
      includeSeeded: boolean = false,
      // EP-16: the technology facet is part of the fetch, so it keys the cache.
      technology: string = "",
      // FEAT-6: the coding-language facet (sorted, comma-joined).
      languages: string = "",
      // Sheet #188: the "Platform library" segment fetches seeds alone.
      seededOnly: boolean = false,
    ) =>
      [
        "library-questions-picker",
        search,
        typeFilter,
        tagKey,
        difficultyKey,
        includeSeeded,
        technology,
        languages,
        seededOnly,
      ] as const,
    detail: (id: string | undefined) => ["library-question", id] as const,
    titleConflicts: (debounced: string, excludeId: string) =>
      ["question-title-conflicts", debounced, excludeId] as const,
    seededTechnologies: () => ["seeded-technologies"] as const,
    // Candidate-style preview of ONE library question (the Preview modal).
    // Distinct namespace from the per-test `candidatePreview(token)` above.
    candidatePreview: (id: string) =>
      ["library-question-candidate-preview", id] as const,
  },
  // NOTE: there is deliberately no `testStats` key. The dashboard card used to
  // fetch GET /api/admin/{id}/stats per card — one request per rendered card —
  // even though /api/admin/tests already returned the same counts for every
  // test in one response. The card now reads the list payload; if you need
  // per-card data, add it to WorkspaceTestItem rather than reviving the N+1.
  testQuestions: {
    all: () => ["test-questions"] as const,
    byToken: (token: string) => ["test-questions", token] as const,
  },
  // Ready-made test templates (MVP 4.0 item 2). One unparameterised list per
  // SOURCE (CP-8: "general" = Global Library, "custom" = this company's own)
  // — the gallery derives its facet pills client-side from each. `all()`
  // stays the invalidation target for save / edit / delete of a custom one.
  templates: {
    all: () => ["templates"] as const,
    list: (source: "general" | "custom" = "general") =>
      ["templates", "list", source] as const,
    preview: (id: string) => ["templates", "preview", id] as const,
  },
  // Integrations (MVP 4.0 item 7) — owner-only Settings → Integrations tab.
  integrations: {
    keys: () => ["integrations", "keys"] as const,
    webhooks: () => ["integrations", "webhooks"] as const,
    eventTypes: () => ["integrations", "event-types"] as const,
    deliveries: (endpointId: string) =>
      ["integrations", "deliveries", endpointId] as const,
    slackOAuth: () => ["integrations", "slack-oauth"] as const,
    // FEAT-47: the "Connected AI assistants" card.
    mcpConnections: () => ["integrations", "mcp-connections"] as const,
    mcpConnectionActivity: (id: string) =>
      ["integrations", "mcp-connections", id, "activity"] as const,
  },
  // ATS integration (Traffit, MVP 4.0 item 7) — owner-only, same tab.
  ats: {
    status: () => ["ats", "status"] as const,
    // Prefix covering BOTH picker caches (recruitments + every states(id)) —
    // invalidate it on connect/disconnect so a previous connection's live
    // Traffit lists can't leak into a new one's rule builder.
    pickers: () => ["ats", "traffit"] as const,
    recruitments: () => ["ats", "traffit", "recruitments"] as const,
    states: (recruitmentId: string) =>
      ["ats", "traffit", "states", recruitmentId] as const,
    // Activity & health feed on the dedicated Traffit page.
    events: () => ["ats", "events"] as const,
    notes: () => ["ats", "notes"] as const,
  },
};
